import React, { useEffect, useState } from 'react';

interface SessionTimeoutWarningProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Remaining time in milliseconds */
  remainingTime: number;
  /** Callback when user clicks to extend session */
  onExtendSession: () => void;
  /** Callback when user clicks to logout */
  onLogout: () => void;
}

/**
 * Modal component that warns users about impending session timeout
 */
export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  visible,
  remainingTime,
  onExtendSession,
  onLogout,
}) => {
  const [seconds, setSeconds] = useState(Math.ceil(remainingTime / 1000));

  useEffect(() => {
    setSeconds(Math.ceil(remainingTime / 1000));
  }, [remainingTime]);

  if (!visible) return null;

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease-out',
        }}
        onClick={onExtendSession}
      />
      
      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'var(--color-bg-container, #ffffff)',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '420px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease-out',
        }}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
      >
        {/* Warning Icon */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(250, 173, 20, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#faad14"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2
          id="session-timeout-title"
          style={{
            margin: '0 0 12px 0',
            fontSize: '20px',
            fontWeight: 600,
            textAlign: 'center',
            color: 'var(--color-text-primary, #1f1f1f)',
          }}
        >
          Session Timeout Warning
        </h2>

        {/* Description */}
        <p
          id="session-timeout-description"
          style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            textAlign: 'center',
            color: 'var(--color-text-secondary, #666666)',
            lineHeight: 1.5,
          }}
        >
          Your session will expire due to inactivity.
        </p>

        {/* Countdown */}
        <p
          style={{
            margin: '0 0 24px 0',
            fontSize: '24px',
            fontWeight: 700,
            textAlign: 'center',
            color: seconds <= 30 ? '#ff4d4f' : '#faad14',
            fontFamily: 'monospace',
          }}
        >
          {formatTime(seconds)}
        </p>

        {/* Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={onLogout}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              border: '1px solid var(--color-border, #d9d9d9)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary, #666666)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#ff4d4f';
              e.currentTarget.style.color = '#ff4d4f';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border, #d9d9d9)';
              e.currentTarget.style.color = 'var(--color-text-secondary, #666666)';
            }}
          >
            Logout Now
          </button>
          <button
            onClick={onExtendSession}
            autoFocus
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#1890ff',
              color: 'var(--color-text-inverse)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#40a9ff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#1890ff';
            }}
          >
            Stay Logged In
          </button>
        </div>

        {/* Hint text */}
        <p
          style={{
            margin: '16px 0 0 0',
            fontSize: '12px',
            textAlign: 'center',
            color: 'var(--color-text-tertiary, #999999)',
          }}
        >
          Click anywhere or press any key to stay logged in
        </p>
      </div>

      {/* Keyframes for animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default SessionTimeoutWarning;
